import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../AuthContext';
import './MasterBulkTrashPanel.css';

/**
 * Standalone master confirmation UI. Authorization, scope validation, password
 * verification and 30-day retention must also be enforced by the API adapter.
 * onPreview() returns {count, confirmationText?, summary?: [{label, value}],
 * requiredPhrase?, ...opaqueMetadata}. The complete preview is passed unchanged
 * to onConfirm({password, confirmed: true, preview}). No API is called here.
 */
export default function MasterBulkTrashPanel({ moduleLabel, onPreview, onConfirm, onCompleted, disabled = false, actionLabel = 'Excluir registros' }) {
  const { user } = useAuth();
  const isMaster = user?.isMaster === true;
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const generation = useRef(0);
  const busy = useRef(false);
  const dialog = useRef(null);
  const trigger = useRef(null);
  const id = useId();

  const resetCredentials = () => { setPassword(''); setConfirmed(false); setPhrase(''); };
  const close = () => {
    if (busy.current) return;
    generation.current += 1;
    resetCredentials(); setOpen(false); setPreview(null); setError(''); setLoading(false);
    trigger.current?.focus();
  };
  const loadPreview = async () => {
    const request = ++generation.current;
    resetCredentials(); setPreview(null); setLoading(true);
    try {
      const result = await onPreview();
      if (request !== generation.current) return;
      if (!Number.isSafeInteger(result?.count) || result.count < 0) throw new Error('Invalid preview');
      setPreview(result);
    } catch (_) {
      if (request === generation.current) setError('Não foi possível consultar os registros. Tente novamente.');
    } finally {
      if (request === generation.current) setLoading(false);
    }
  };
  useEffect(() => () => { generation.current += 1; }, []);
  useEffect(() => {
    if (!isMaster) {
      generation.current += 1;
      setOpen(false); setPassword(''); setConfirmed(false); setPhrase(''); setPreview(null);
    }
  }, [isMaster]);
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    dialog.current?.focus();
    const keepFocusInside = event => {
      if (dialog.current && !dialog.current.contains(event.target)) dialog.current.focus();
    };
    document.addEventListener('focusin', keepFocusInside);
    return () => { document.removeEventListener('focusin', keepFocusInside); previous?.focus?.(); };
  }, [open]);

  if (!isMaster) return null;
  const valid = preview?.count > 0 && password.length > 0 && confirmed &&
    (!preview.requiredPhrase || phrase === preview.requiredPhrase) && !loading && !submitting && !disabled;
  const submit = async event => {
    event.preventDefault();
    if (!valid || busy.current) return;
    const request = generation.current;
    const credentials = password;
    busy.current = true; setSubmitting(true); setError(''); resetCredentials();
    let result;
    try {
      result = await onConfirm({ password: credentials, confirmed: true, preview });
    } catch (failure) {
      if (request !== generation.current) return;
      if ((failure?.response?.status ?? failure?.status) === 409) {
        setError('Os registros foram alterados. Confira o novo resumo e confirme novamente.');
        await loadPreview();
        if (generation.current === request + 1) setSubmitting(false);
      } else setError('Não foi possível enviar os registros para a lixeira. Confira a senha e tente novamente.');
      return;
    } finally {
      busy.current = false;
      if (request === generation.current) setSubmitting(false);
    }
    if (request !== generation.current) return;
    close();
    setNotice(`${result?.deletedCount ?? preview.count} registros enviados para a lixeira por 30 dias.`);
    try {
      await onCompleted?.(result);
    } catch (_) {
      setNotice('Registros enviados para a lixeira por 30 dias, mas não foi possível atualizar a tela. Recarregue a página para consultar os dados atuais.');
    }
  };
  const handleKeys = event => {
    if (event.key === 'Escape') { event.preventDefault(); close(); }
    if (event.key !== 'Tab') return;
    const nodes = dialog.current.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]');
    if (!nodes.length) { event.preventDefault(); dialog.current.focus(); return; }
    const first = nodes[0]; const last = nodes[nodes.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog.current)) {
      event.preventDefault(); first.focus();
    }
  };
  return <section className="master-bulk-trash-panel" aria-label={`Lixeira: ${moduleLabel}`}>
    <button ref={trigger} className="master-bulk-trash-danger" type="button" disabled={disabled} onClick={() => {
      setOpen(true); setError(''); setNotice(''); loadPreview();
    }}>{actionLabel}</button>
    {notice && <p role="status">{notice}</p>}
    {open && createPortal(<div className="master-bulk-trash-backdrop">
      <div ref={dialog} className="master-bulk-trash-dialog" role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`} tabIndex={-1} onKeyDown={handleKeys}>
        <header><h2 id={`${id}-title`}>Enviar registros para a lixeira</h2><button type="button" aria-label="Fechar" disabled={submitting} onClick={close}>×</button></header>
        <form onSubmit={submit} className="master-bulk-trash-body">
          <p id={`${id}-description`}>Escopo: <strong>{moduleLabel}</strong>. Os registros enviados para a lixeira ficarão disponíveis para recuperação por 30 dias. Após esse prazo, poderão ser excluídos permanentemente.</p>
          {loading && <p role="status">Consultando registros...</p>}
          {preview && <><p><strong>{preview.count} registros</strong></p>
            {Array.isArray(preview.summary) && <dl>{preview.summary.map((item, index) => <React.Fragment key={index}><dt>{item.label}</dt><dd>{item.value}</dd></React.Fragment>)}</dl>}
            {preview.confirmationText && <p>{preview.confirmationText}</p>}
          </>}
          {error && <p role="alert" className="master-bulk-trash-error">{error}</p>}
          {!loading && !preview && <button type="button" onClick={() => { setError(''); loadPreview(); }}>Tentar novamente</button>}
          <label htmlFor={`${id}-password`}>Senha atual do master</label>
          <input id={`${id}-password`} type="password" autoComplete="current-password" value={password} disabled={loading || submitting} onChange={event => setPassword(event.target.value)} />
          {preview?.requiredPhrase && <><label htmlFor={`${id}-phrase`}>Digite: {preview.requiredPhrase}</label><input id={`${id}-phrase`} value={phrase} disabled={submitting} onChange={event => setPhrase(event.target.value)} /></>}
          <label className="master-bulk-trash-check"><input type="checkbox" checked={confirmed} disabled={loading || submitting || !preview} onChange={event => setConfirmed(event.target.checked)} />Confirmo o envio dos registros do escopo acima para a lixeira por 30 dias.</label>
          <footer><button type="button" disabled={submitting} onClick={close}>Cancelar</button><button type="submit" className="master-bulk-trash-danger" disabled={!valid}>{submitting ? 'Enviando...' : 'Enviar para a lixeira'}</button></footer>
        </form>
      </div>
    </div>, document.body)}
  </section>;
}
